import { GraphQLClient } from 'graphql-request'
import { poolsQuery, userPools } from './queries/basics'
import {
  Endpoints,
  PoolQueryResult,
  SwapByPoolQueryResult,
  DailySwapValue,
  PoolWithData,
} from '../api-types'
import { h24Swaps } from './queries/swaps'
import { Address, Wei } from '../../utils/types'
import { MAX_ITEMS_PER_QUERY, SWAP_FEE_WINDOW } from '../api-parameters'
import { oracleApi } from '../oracle/oracle-client'
import { reorderTokens } from '../../logic/pools/reorder-tokens'
import { floorWei, fromWei, toWei } from '../../utils/static-web3'
import { bigIntToWei } from '../../logic/financials/big-int-to-wei'
import { getWhitelistPools, protocolPrecision } from '../../config'

let subgraphEndpoint: string | undefined = undefined

export let cachedClient: GraphQLClient

export function configureEndpoints(endpoints: Endpoints) {
  if (subgraphEndpoint) {
    throw 'Subgraph endpoint already configured'
  }
  if (!endpoints.subgraph) {
    throw 'Subgraph not setup in .env'
  }

  subgraphEndpoint = endpoints.subgraph
  cachedClient = new GraphQLClient(subgraphEndpoint, {
    mode: 'cors',
    headers: {},
  })
}

export const graphApi = {
  getPools: async function (): Promise<PoolWithData[]> {
    try {
      let rawRequest = await cachedClient.request(poolsQuery)

      const result: PoolQueryResult[] = rawRequest.pools.filter(
        (pool: PoolQueryResult) => {
          return getWhitelistPools().includes(pool.id.toLowerCase())
        }
      )
      const prices = await oracleApi.getPrices()

      // TODO: check for correct typing - warning with typescript 4.5.5
      return result.map(p => {
        const pool: PoolWithData = {
          tokens: reorderTokens(p.tokens, p.tokensList).map(t => {
            const price = prices.find(p => p.symbol === t.symbol)!.price
            return {
              ...t,
              balance: toWei(Number(t.balance), t.decimals),
              value: Number(t.balance) * price,
            }
          }),
          address: p.id.toLowerCase(),
          tvl: Number(p.liquidity),
          fees: {
            dailyFees: Number(p.dailyActivity.dailyFees),
            dailyVolume: Number(p.dailyActivity.dailyVolume),
            swapCount: Number(p.dailyActivity.swapCount),
          },
          finalized: p.finalized,
          totalShares: toWei(Number(p.totalShares), protocolPrecision),
          totalSwapVolume: Number(p.totalSwapVolume),
          controller: p.controller,
          publicSwap: p.publicSwap,
          initialPoolTokenPrice: Number(p.lpTokenPrices.find(ptp => String(ptp.id).includes("-initial"))!.price),
          currentPoolTokenPrice: Number(p.lpTokenPrices.find(ptp => String(ptp.id).includes("-current"))!.price),
          createTimeMs: p.createTime * 1000
        }

        // We need to recalculate because TheGraph don't have oracle prices
        pool.tvl = pool.tokens.reduce((acc, next) => {
          return acc + next.value
        }, 0)

        // TODO: approximate with 3 digits, and make sure total makes 100
        pool.tokens.forEach(t => {
          t.percent =
            ((fromWei(t.balance, t.decimals) * oracleApi.getPrice(t.symbol)) /
              pool.tvl) *
            100
        })

        return pool
      })
    } catch (e) {
      console.error(e)
      // TODO: ErrorBoundary case
      return Promise.resolve([])
    }
  },
  getUserPoolValue: async function (
    userAddress: Address,
    pool: PoolWithData
  ): Promise<{ value: number; shares: Wei }> {
    const queryResult = await cachedClient.request(userPools, {
      poolAddress: pool.address.toLowerCase(),
      userAddress: userAddress.toLowerCase(),
    })
    const userSharesArray = queryResult.pool.shares
    let userSharesWei = ''
    let userSharesNumber = 0

    if (userSharesArray.length > 1) {
      userSharesArray.forEach(
        (s: any) => (userSharesNumber += Number(s.balance))
      )
      userSharesWei = floorWei(userSharesNumber)
    } else if (userSharesArray.length === 1) {
      // We don't want to translate as number for keeping precision
      userSharesWei = bigIntToWei(userSharesArray[0].balance, protocolPrecision)
      userSharesNumber = Number(userSharesArray[0].balance)
    } else {
      userSharesWei = '0'
      userSharesNumber = 0
    }

    const value =
      pool.tvl *
      (userSharesNumber / fromWei(pool.totalShares, protocolPrecision))

    return {
      value,
      shares: userSharesWei,
    }
  },

  /**
   * @param pool
   */
  getDailySwaps: async function (pool: Address): Promise<DailySwapValue> {
    const tsNow = Math.round(new Date().getTime() / 1000)
    const tsYesterday = Math.round(tsNow - SWAP_FEE_WINDOW)

    const swapValue: DailySwapValue = {
      swaps: [],
      volume: '0',
      fees: '0',
      count: 0,
    }

    let size = 0
    let skip = 0
    do {
      const queryResult: SwapByPoolQueryResult = await cachedClient.request(
        h24Swaps(pool, skip, tsYesterday, tsNow),
        {}
      )
      size = queryResult.swaps.length
      skip += size
      swapValue.swaps = swapValue.swaps.concat(queryResult.swaps)
      swapValue.count += size
      // TODO: Need a lib instead of parseFloat
      swapValue.volume =
        parseFloat(swapValue.volume) +
        queryResult.swaps.reduce(
          (acc, next) => acc + parseFloat(next.value),
          0
        ) +
        ''
      swapValue.fees =
        parseFloat(swapValue.fees) +
        queryResult.swaps.reduce(
          (acc, next) => acc + parseFloat(next.feeValue),
          0
        ) +
        ''
    } while (size === MAX_ITEMS_PER_QUERY)
    return swapValue
  },
}
